import { RadioTileGroup } from '@noissue-ui-kit/oldBranding/radioTile'
import { useWidget } from '../EditorDataProvider'
import { HiMinus, HiPlus } from 'react-icons/hi'
import {
  offsetMapper,
  OffsetPatternImage,
  PlainPatternImage,
} from './PatternSettings.utils'
import { PositionOffsetButtons } from './PositionOffsetArrows'
import { TPatternWidgetVariant } from '../Widgets/PatternWidget'
import { PATTERN_VARIANTS } from '../editor.config'
import { HiArrowUturnLeft, HiArrowUturnRight } from 'react-icons/hi2'

const ROTATION_STEP = 15

export function PatternSettings({
  patternVariant = PATTERN_VARIANTS.TILE,
}: {
  patternVariant: TPatternWidgetVariant
}) {
  const { widget, updateWidget } = useWidget('pattern')
  const titleClassName = 'mt-6 mb-3 text-lg font-semibold text-acai select-none'
  const restrictPositionCustomisation = patternVariant === PATTERN_VARIANTS.LINE

  return (
    <>
      <h5 className="mb-4 text-2xl font-semibold text-acai select-none">
        Customize
      </h5>

      <div className="overflow-auto scrollbar-thin pr-2.5 flex flex-wrap justify-between">
        {!restrictPositionCustomisation && (
          <div className="flex flex-wrap justify-between w-full">
            <h5 className={titleClassName}>Offset</h5>

            <RadioTileGroup
              onChange={(patternStyle) => {
                updateWidget({
                  offset: patternStyle.key === 'offset',
                })
              }}
              options={[
                {
                  displayLabel: <PlainPatternImage />,
                  key: 'plain',
                },
                {
                  displayLabel: <OffsetPatternImage />,
                  key: 'offset',
                },
              ]}
              radioGroupClassName="grid-cols-2 w-full"
              radioItemClassName="p-0 hover:bg-black-10"
              selected={offsetMapper(widget?.config?.offset)}
            />
          </div>
        )}

        <div className="flex flex-wrap justify-between w-full">
          <h5 className={titleClassName}>Rotate</h5>

          <RadioTileGroup
            onChange={(rotationSide) => {
              const currentRotation = widget?.config?.rotationAngle || 0
              updateWidget({
                rotationAngle:
                  rotationSide?.key === '+'
                    ? currentRotation + ROTATION_STEP
                    : currentRotation - ROTATION_STEP,
              })
            }}
            options={[
              {
                displayLabel: <HiArrowUturnLeft className="stroke-1" />,
                key: '-',
              },
              {
                displayLabel: <HiArrowUturnRight className="stroke-1" />,
                key: '+',
              },
            ]}
            radioGroupClassName="grid-cols-2 w-full"
            radioItemClassName="hover:bg-black-10"
          />
        </div>

        {!restrictPositionCustomisation && (
          <div className="grid grid-cols-1 gap-x-3 w-[calc(50%-4px)]">
            <h5 className={titleClassName}>Vertical Spacing</h5>

            <RadioTileGroup
              onChange={(scale) => {
                const scaleStep = scale.key === '+' ? 1 : -1
                const currentStepCount =
                  widget?.config?.verticalMarginStepCount || 0
                if (currentStepCount <= 0 && scaleStep === -1) {
                  return
                }
                updateWidget({
                  verticalMarginStepCount: currentStepCount + scaleStep,
                })
              }}
              options={[
                {
                  displayLabel: <HiPlus />,
                  key: '+',
                },
                {
                  displayLabel: <HiMinus />,
                  key: '-',
                  disabled: (widget?.config?.verticalMarginStepCount || 0) <= 0,
                },
              ]}
              radioGroupClassName="grid-cols-2 w-full"
              radioItemClassName="hover:bg-black-10"
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-x-3 w-[calc(50%-4px)]">
          <h5 className={titleClassName}>Horizontal Spacing</h5>

          <RadioTileGroup
            onChange={(scale) => {
              const scaleStep = scale.key === '+' ? 1 : -1
              const currentStepCount =
                widget?.config?.horizontalMarginStepCount || 0
              if (currentStepCount <= 0 && scaleStep === -1) {
                return
              }
              updateWidget({
                horizontalMarginStepCount: currentStepCount + scaleStep,
              })
            }}
            options={[
              {
                displayLabel: <HiPlus />,
                key: '+',
              },
              {
                displayLabel: <HiMinus />,
                key: '-',
                disabled: (widget?.config?.horizontalMarginStepCount || 0) <= 0,
              },
            ]}
            radioGroupClassName="grid-cols-2 w-full"
            radioItemClassName="hover:bg-black-10"
          />
        </div>

        <div className="grid grid-cols-1 gap-x-3 w-[calc(50%-4px)]">
          <h5 className={titleClassName}>Scale</h5>
          <RadioTileGroup
            onChange={(scale) => {
              const scaleStep = scale.key === '+' ? 1 : -1
              updateWidget({
                scaleStepCount: widget.config.scaleStepCount + scaleStep,
              })
            }}
            options={[
              {
                displayLabel: <HiPlus />,
                key: '+',
              },
              {
                displayLabel: <HiMinus />,
                key: '-',
              },
            ]}
            radioGroupClassName="grid-cols-2 w-full"
            radioItemClassName="hover:bg-black-10"
          />
        </div>

        {!restrictPositionCustomisation && (
          <div className="grid grid-cols-1 gap-x-3 w-[calc(50%-4px)]">
            <h5 className={titleClassName}>Pattern Position</h5>

            <PositionOffsetButtons />
          </div>
        )}
      </div>
    </>
  )
}
